import React, { useState, useMemo } from 'react';
import { X, Search, MapPin } from 'lucide-react';

/**
 * Modal that displays a searchable list of deliveries for a given status/category.
 *
 * @param {boolean}  isOpen        - Controls visibility
 * @param {function} onClose       - Close handler
 * @param {string}   title         - Modal title (e.g. "Entregados")
 * @param {Array}    limaItems     - Lima deliveries to show
 * @param {Array}    provinciaItems - Provincia deliveries to show
 * @param {Array}    tabs          - Optional tab config for "En Proceso": [{ label, items }]
 */
export function DeliveryListModal({ isOpen, onClose, title, limaItems = [], provinciaItems = [], tabs = null }) {
  const [search, setSearch] = useState('');

  // Determine if we're in "custom tabs" mode (e.g. En Proceso) or standard Lima/Provincia mode
  const isCustomTabs = tabs !== null;

  // Standard Lima/Provincia tabs — only show a tab if it has data
  const standardTabs = useMemo(() => {
    const result = [];
    if (limaItems.length > 0) result.push({ label: 'Lima', items: limaItems });
    if (provinciaItems.length > 0) result.push({ label: 'Provincia', items: provinciaItems });
    return result;
  }, [limaItems, provinciaItems]);

  const activeTabs = isCustomTabs ? tabs : standardTabs;

  const [activeTabIndex, setActiveTabIndex] = useState(0);

  // Reset tab + search when modal opens
  React.useEffect(() => {
    if (isOpen) {
      setActiveTabIndex(0);
      setSearch('');
    }
  }, [isOpen]);

  const currentItems = activeTabs[activeTabIndex]?.items ?? [];

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return currentItems;
    return currentItems.filter(d =>
      String(d.id).includes(q) ||
      (d.recipient_name ?? d.name ?? '').toLowerCase().includes(q) ||
      (d.recipient_phone ?? d.phone ?? '').toLowerCase().includes(q) ||
      (d.district ?? d.recipient_district ?? '').toLowerCase().includes(q)
    );
  }, [currentItems, search]);

  if (!isOpen) return null;

  return (
    // Backdrop
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.45)' }}
      onClick={onClose}
    >
      {/* Panel */}
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[80vh] flex flex-col overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <h2 className="text-base font-extrabold text-slate-900 font-montserrat italic">
            {title}
            <span className="ml-2 text-sm font-semibold text-slate-400 not-italic">
              ({currentItems.length})
            </span>
          </h2>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-slate-100 transition-colors"
          >
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>

        {/* Tabs — only render if more than one tab */}
        {activeTabs.length > 1 && (
          <div className="flex border-b border-slate-200 px-5">
            {activeTabs.map((tab, i) => (
              <button
                key={tab.label}
                onClick={() => { setActiveTabIndex(i); setSearch(''); }}
                className={`py-2 px-4 text-sm font-semibold font-montserrat border-b-2 transition-colors ${
                  activeTabIndex === i
                    ? 'border-[#B1336E] text-[#B1336E]'
                    : 'border-transparent text-slate-500 hover:text-slate-700'
                }`}
              >
                {tab.label}
                <span className={`ml-1.5 text-xs px-1.5 py-0.5 rounded-full ${
                  activeTabIndex === i ? 'bg-[#B1336E]/10 text-[#B1336E]' : 'bg-slate-100 text-slate-500'
                }`}>
                  {tab.items.length}
                </span>
              </button>
            ))}
          </div>
        )}

        {/* Search */}
        <div className="px-5 py-3 border-b border-slate-100">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder="Buscar por ID, nombre, teléfono o distrito..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B1336E]/30 focus:border-[#B1336E] font-montserrat"
            />
          </div>
        </div>

        {/* List */}
        <div className="overflow-y-auto flex-1">
          {filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-slate-400">
              <Search className="w-8 h-8 mb-2 opacity-40" />
              <p className="text-sm font-montserrat">Sin resultados</p>
            </div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {filtered.map((d, idx) => {
                const name = d.recipient_name ?? d.name ?? '—';
                const phone = d.recipient_phone ?? d.phone ?? '—';
                const district = d.district ?? d.recipient_district ?? '—';

                return (
                  <li
                    key={d.id ?? idx}
                    className="flex items-center gap-3 px-5 py-3 hover:bg-slate-50 transition-colors"
                  >
                    {/* ID badge */}
                    <span className="shrink-0 text-xs font-bold text-[#B1336E] bg-[#B1336E]/10 px-2 py-1 rounded font-montserrat min-w-[2.5rem] text-center">
                      #{d.id}
                    </span>

                    {/* Name + phone */}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-800 font-montserrat truncate">{name}</p>
                      <p className="text-xs text-slate-500 font-montserrat">{phone}</p>
                    </div>

                    {/* District */}
                    <div className="flex items-center gap-1 shrink-0 text-xs text-slate-500 font-montserrat">
                      <MapPin className="w-3 h-3" />
                      <span className="truncate max-w-[7rem]">{district}</span>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}